import { useTranslation } from 'react-i18next';
import {
  useUsersQuery,
  useAllProjectsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from 'src/queries';
import { useModal } from 'src/hooks/useModal';
import type { User } from 'src/types';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import EntityModal from '../components/EntityModal';
import ConfirmDialog from '../components/ConfirmDialog';
import UserForm from '../forms/UserForm';

const UsersTab = () => {
  const { t } = useTranslation();
  const { data: users = [], isLoading } = useUsersQuery();
  const { data: projects = [] } = useAllProjectsQuery();

  const createMutation = useCreateUserMutation();
  const updateMutation = useUpdateUserMutation();
  const deleteMutation = useDeleteUserMutation();

  const formModal = useModal<User>();
  const deleteModal = useModal<User>();

  const columns: Column<User>[] = [
    { key: 'name', header: t('management.user.name') },
    { key: 'email', header: t('management.user.email') },
    {
      key: 'is_admin',
      header: t('management.user.isAdmin'),
      render: (u) => (u.is_admin ? 'Yes' : 'No'),
    },
    {
      key: 'project_ids',
      header: t('management.user.projects'),
      render: (u) => String(u.project_ids?.length ?? 0),
    },
  ];

  const handleCreate = (data: Partial<User>) => {
    createMutation.mutate(data, { onSuccess: () => formModal.close() });
  };

  const handleUpdate = (data: Partial<User>) => {
    if (!formModal.editingItem) return;
    updateMutation.mutate(
      { id: formModal.editingItem.id, data },
      { onSuccess: () => formModal.close() }
    );
  };

  const handleDelete = () => {
    if (!deleteModal.editingItem) return;
    deleteMutation.mutate(deleteModal.editingItem.id, {
      onSuccess: () => deleteModal.close(),
    });
  };

  const isEditing = !!formModal.editingItem;

  return (
    <>
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        onCreate={formModal.openForCreate}
        onEdit={(user) => formModal.openForEdit(user)}
        onDelete={(user) => deleteModal.openForEdit(user)}
        createLabel={t('management.users')}
      />

      <EntityModal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? t('management.edit') : t('management.create')}
      >
        <UserForm
          initialData={formModal.editingItem ?? undefined}
          projects={projects}
          onSubmit={isEditing ? handleUpdate : handleCreate}
          isLoading={isEditing ? updateMutation.isPending : createMutation.isPending}
          onCancel={formModal.close}
        />
      </EntityModal>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onConfirm={handleDelete}
        onCancel={deleteModal.close}
        title={t('management.confirmDeleteTitle')}
        message={t('management.confirmDeleteMessage', {
          name: deleteModal.editingItem?.name ?? '',
        })}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default UsersTab;
